import React, { useEffect, useState } from 'react';
import { ArrowRight, Pause } from 'lucide-react';
import { scrollToSection, isConfigured } from '@/lib/scroll';
import { DEFAULT_SETTINGS, SiteSettings } from '@/hooks/useSiteContent';
import { jukeboxBus, JukeboxState } from '@/lib/jukeboxBus';

function Dotted({ items }: { items: string[] }) {
  return (
    <>
      {items.map((t, i) => (
        <React.Fragment key={i}>
          {i > 0 && <i aria-hidden="true" />}
          {t}
        </React.Fragment>
      ))}
    </>
  );
}

export default function Hero({ hero }: { hero: SiteSettings['hero'] }) {
  const [jk, setJk] = useState<JukeboxState>(jukeboxBus.getState());
  useEffect(() => jukeboxBus.onState(setJk), []);

  const canUseJukebox = jk.ready && jk.hasItems;
  const isPlaying = canUseJukebox && jk.playing;

  const onListen = () => {
    if (canUseJukebox) {
      jukeboxBus.fire({ type: 'toggle' });
      return;
    }
    if (isConfigured(hero.listen_now_url)) {
      window.open(hero.listen_now_url, '_blank', 'noopener');
    } else {
      scrollToSection('music');
    }
  };

  const isDefaultCopy = hero.description.trim() === DEFAULT_SETTINGS.hero.description;
  const btnLabel = isPlaying ? 'Pause' : 'Listen Now';

  return (
    <section className="ek-hero" aria-labelledby="hero-title" data-testid="hero-section">
      <p className="ek-hero-loc" data-testid="hero-location">
        <Dotted items={hero.locations.filter(Boolean)} />
      </p>

      <h1 id="hero-title" className="ek-hero-title" data-testid="hero-headline">
        <span className="ek-hero-l1">{hero.headline_line1}</span>
        <span className="ek-hero-l2">
          <span className="ek-grad-text">{hero.headline_line2}</span>
        </span>
      </h1>

      <p className={`ek-hero-copy ${isDefaultCopy ? '' : 'is-custom'}`} data-testid="hero-description">
        {isDefaultCopy ? (
          <>
            The Ekuba Project is a boundary-pushing electronic music <br className="ek-dbr" />
            experience by Ernesto Couso, blending house grooves, <br className="ek-dbr" />
            tech-house pulses, and Latin melodic textures.
          </>
        ) : (
          hero.description
        )}
      </p>

      <div className="ek-hero-ctas">
        <button
          type="button"
          className={`ek-btn-primary ${isPlaying ? 'is-playing' : ''}`}
          onClick={onListen}
          aria-pressed={isPlaying}
          aria-label={isPlaying ? 'Pause jukebox' : 'Open jukebox and play'}
          data-testid="hero-listen-now-btn"
        >
          {isPlaying ? (
            <Pause className="ek-play-ico" size={14} strokeWidth={2.5} aria-hidden="true" />
          ) : (
            <svg className="ek-play-ico" viewBox="0 0 14 15" aria-hidden="true">
              <path d="M1.2 1.1c0-.8.9-1.3 1.6-.9l10.3 6.4c.6.4.6 1.3 0 1.7L2.8 14.7c-.7.4-1.6-.1-1.6-.9V1.1z" fill="currentColor" />
            </svg>
          )}
          <span>{btnLabel}</span>
          {isPlaying && (
            <span className="ek-hero-eq" aria-hidden="true"><span /><span /><span /></span>
          )}
        </button>
        <button type="button" className="ek-btn-ghost" onClick={() => scrollToSection('releases')} data-testid="hero-explore-releases-btn">
          <span>Explore Releases</span>
          <ArrowRight className="ek-btn-arrow" size={14} strokeWidth={2} />
        </button>
      </div>

      <p className="ek-hero-tags" data-testid="hero-taglines">
        <Dotted items={hero.taglines.filter(Boolean)} />
      </p>
    </section>
  );
}
